import { useState, useEffect } from 'react';
import { shiftsAPI, staffAPI } from '../../api';
import api from '../../api';

function formatDuration(minutes) {
  if (!minutes) return '0m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}


function exportToCSV(data, filename) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(row => headers.map(h => {
    const val = row[h] === null || row[h] === undefined ? '' : String(row[h]);
    return val.includes(',') || val.includes('"') ? `"${val.replace(/"/g, '""')}"` : val;
  }).join(','));
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function ShiftsPage({ guild }) {
  const [shifts, setShifts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStaff, setFilterStaff] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [departments, setDepartments] = useState([]);
  const [page, setPage] = useState(0);
  const PER_PAGE = 25;
  const sym = guild?.currency_symbol || '$';

  useEffect(() => {
    Promise.all([
      shiftsAPI.getAll(guild.id, { limit: 200 }),
      staffAPI.getAll(guild.id),
      api.get(`/api/departments/${guild.id}`),
    ]).then(([shiftsRes, staffRes, deptsRes]) => {
      setShifts(shiftsRes.data);
      setStaff(staffRes.data);
      setDepartments(deptsRes.data);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [guild.id]);

  const filtered = shifts.filter(s => {
    if (filterStaff && s.discord_id !== filterStaff) return false;
    if (filterDept && s.department_id !== parseInt(filterDept)) return false;
    return true;
  });

  const paginated = filtered.slice(page * PER_PAGE, (page + 1) * PER_PAGE);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);

  // Summary stats
  const totalHours = filtered.reduce((sum, s) => sum + (parseInt(s.duration_minutes) || 0), 0) / 60;
  const totalEarnings = filtered.reduce((sum, s) => sum + parseFloat(s.earnings || 0), 0);
  const avgDuration = filtered.length > 0 ? (filtered.reduce((sum, s) => sum + (parseInt(s.duration_minutes) || 0), 0) / filtered.length) : 0;

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>Loading shifts...
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', color: 'var(--text-primary)', marginBottom: '6px' }}>Shifts</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>{filtered.length} shifts shown</p>
      </div>

      {/* Summary stats */}
      <div className="stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {[
          { icon: '⏱️', label: 'Total Hours', value: `${totalHours.toFixed(1)}h` },
          { icon: '💵', label: 'Total Earnings', value: `${sym}${totalEarnings.toFixed(2)}` },
          { icon: '📊', label: 'Total Shifts', value: filtered.length },
          { icon: '⌛', label: 'Avg Duration', value: formatDuration(Math.round(avgDuration)) },
        ].map(stat => (
          <div key={stat.label} style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: '14px', padding: '20px', boxShadow: 'var(--shadow-card)',
          }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>{stat.icon}</div>
            <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>{stat.value}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <select value={filterDept} onChange={e => { setFilterDept(e.target.value); setPage(0); }}
          style={{
            padding: '10px 16px', border: '2px solid var(--border)', borderRadius: '10px',
            fontSize: '14px', fontFamily: 'var(--font-body)',
            background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
            minWidth: '180px',
          }}>
          <option value="">All Departments</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={filterStaff} onChange={e => { setFilterStaff(e.target.value); setPage(0); }}
          style={{
            padding: '10px 16px', border: '2px solid var(--border)', borderRadius: '10px',
            fontSize: '14px', fontFamily: 'var(--font-body)',
            background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
            minWidth: '200px',
          }}>
          <option value="">All Staff</option>
          {staff.map(s => <option key={s.discord_id} value={s.discord_id}>{s.display_name || s.username}</option>)}
        </select>
      </div>

      {/* Shifts table */}
      <div className="table-scroll">
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 1fr 1fr 1fr',
          padding: '12px 20px', borderBottom: '1px solid var(--border)',
          fontSize: '12px', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.5px', color: 'var(--text-muted)',
        }}>
          <span>Staff Member</span><span>Clock In</span><span>Clock Out</span>
          <span>Duration</span><span>Earned</span><span>Rate</span>
        </div>

        {paginated.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>No shifts found.</div>
        ) : paginated.map(shift => (
          <div key={shift.id}
            style={{
              display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 1fr 1fr 1fr',
              padding: '14px 20px', borderBottom: '1px solid var(--border)',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                background: 'var(--gradient-accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontWeight: 700, color: '#fff',
              }}>
                {(shift.display_name || shift.username || '?')[0].toUpperCase()}
              </div>
              <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
                {shift.display_name || shift.username}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', fontSize: '13px', color: 'var(--text-secondary)' }}>
              {new Date(shift.clock_in).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', fontSize: '13px', color: 'var(--text-secondary)' }}>
              {shift.clock_out
                ? new Date(shift.clock_out).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                : <span style={{ color: 'var(--success)', fontWeight: 600 }}>🟢 Active</span>
              }
            </div>
            <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: 'var(--text-primary)' }}>
              {formatDuration(shift.duration_minutes)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
              {shift.clock_out ? `${sym}${parseFloat(shift.earnings || 0).toFixed(2)}` : '—'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-muted)' }}>
              {shift.department_name ? (
                <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '100px', background: (shift.department_color || '#5865F2') + '20', color: shift.department_color || '#5865F2', border: `1px solid ${shift.department_color || '#5865F2'}40`, fontWeight: 600 }}>
                  {shift.department_name}
                </span>
              ) : <span>{sym}{parseFloat(shift.hourly_rate || 0).toFixed(2)}/hr</span>}
            </div>
          </div>
        ))}
      </div>

      </div>{/* end table-scroll */}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            style={{
              padding: '8px 16px', borderRadius: '8px', cursor: page === 0 ? 'not-allowed' : 'pointer',
              border: '1px solid var(--border)', background: 'var(--bg-card)',
              color: 'var(--text-secondary)', fontFamily: 'var(--font-body)', opacity: page === 0 ? 0.5 : 1,
            }}>← Prev</button>
          <span style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: 'var(--text-secondary)', padding: '0 12px' }}>
            Page {page + 1} of {totalPages}
          </span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page === totalPages - 1}
            style={{
              padding: '8px 16px', borderRadius: '8px', cursor: page === totalPages - 1 ? 'not-allowed' : 'pointer',
              border: '1px solid var(--border)', background: 'var(--bg-card)',
              color: 'var(--text-secondary)', fontFamily: 'var(--font-body)', opacity: page === totalPages - 1 ? 0.5 : 1,
            }}>Next →</button>
        </div>
      )}
    </div>
  );
}
